/* eslint-disable react/prop-types */
import React from "react";
import { Box, Typography } from "@material-ui/core";
import cloudStorageOptions from "components/cloudStorageOptions";
import { sourceLabels } from "pages/Accounts/SourceList/helpers";
import { TFunction, useTranslation } from "react-i18next";
import {
  EventItem,
  StorageAccountEvent,
  Event,
  SourceEvent,
  StorageEvent,
  SecurityEvent,
  UserUpdatedEvent,
  StorageAccountCreatedEvent,
  StorageAccountReauthorizedEvent,
  OrganizationEvent,
  DomainEvent,
  LoginOptions,
  LegacySecurityUpdatedData,
  LegacyOrganizationUpdatedData,
  LegacySecurityPolicyUpdatedData,
  LegacyTemplateDefinitionUpdatedData,
} from "@dashboard-v3/api";
import styled from "styled-components";

export default function Affected({ userEvent }: { userEvent: EventItem }) {
  const { t } = useTranslation(["userActivity", "users"]);
  return (
    <Box className="MuiTypography-root MuiTypography-body1">
      {label(userEvent, t)}
    </Box>
  );
}

const AffectedList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding-inline-start: 0;
`;

const getEmail = (event: Event): string => {
  if ("email" in event.data) {
    return event.data.email;
  }
  return "";
};

const sourceChanges = (event: SourceEvent, t) => {
  const source = sourceLabels[event.type];
  if ("label" in event.data && event.data.label) {
    return `${source} ${t("change.withLabel", { label: event.data.label })}`;
  }

  return source;
};

function resolveDomainName(event: DomainEvent): string {
  if ("domain" in event.data) {
    return event.data.domain || "";
  }
  return "";
}

function label(
  eventItem: EventItem,
  t: TFunction<"userActivity" | "users">
): React.ReactNode {
  const { id, event } = eventItem;
  const { type } = event;

  switch (type) {
    case "login":
      return t(`change.login.${event.data.method}`);

    case "account_deleted":
    case "account_created":
    case "account_updated":
      return getEmail(event);

    case "source_created":
    case "source_deleted":
    case "source_updated":
      return sourceChanges(event, t);

    case "storage_account_created":
    case "storage_account_reauthorized":
      return storageAccountAuth(event, t);

    case "storage_account_deleted":
      return storageAccountDeleted(event, t);

    case "storage_settings_updated":
      return storageSettingsUpdated(event, t);

    case "organization_updated":
      return organizationUpdated(event, t);

    case "domain_deleted":
    case "domain_created":
    case "domain_updated":
      return resolveDomainName(event);

    case "security_updated":
      return securityUpdated(event, t, id);

    case "user_updated":
      return userUpdated(event, t);

    case "user_created":
    case "user_deleted":
    case "reset_password":
      return getEmail(event);

    case "security_policy_updated":
    case "template_definition_updated":
      return `${t("change.withName")} ${renamedTo(
        namedPayload(event.data),
        t
      )}`;

    case "template_definition_created":
    case "template_definition_deleted":
    case "security_definition_created":
    case "security_definition_updated":
    case "security_definition_deleted":
    case "template_policy_created":
    case "template_policy_updated":
    case "template_policy_deleted":
    case "security_policy_created":
    case "security_policy_deleted":
    case "filter_policy_created":
    case "filter_policy_deleted":
    case "filter_policy_updated":
    case "variable_created":
    case "variable_deleted":
    case "variable_updated":
    case "dataset_created":
    case "dataset_deleted":
    case "dataset_updated":
    case "copy_policy_created":
    case "copy_policy_deleted":
    case "copy_policy_updated":
    case "group_deleted":
      return `${t("change.withName")} ${renamedTo(event.data, t)}`;

    case "rule_created":
    case "rule_deleted":
    case "rule_updated":
    case "rule_enabled":
    case "rule_disabled":
      return `${t("change.withName")} ${event.data.name}`;

    case "role_updated":
      return t("change.role_updated", {
        role: t(`users:userRole.${event.data.role}`),
      });

    case "malware_released":
      return t("change.malware_released", {
        threat: event.data.threats.join(", "),
      });

    default:
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled event type: ${exhaustiveCheck}`);
  }
}

/**
 * N2 entities record the name they had when the event happened, plus
 * `previousName` when that very event renamed them.
 */
function renamedTo(
  data: { name: string; previousName?: string },
  t: TFunction<"userActivity" | "users">
): string {
  if (data.previousName) {
    return t("change.renamed", { from: data.previousName, to: data.name });
  }
  return data.name;
}

function storageSettingsUpdated(event: StorageEvent, t) {
  const { type } = event.data;

  if (type === "readable_text") {
    return t(`change.readable_text.${event.data.change}`);
  }

  return t(`change.${type}`);
}

function storageAccountDeleted(event: StorageAccountEvent, t) {
  const { user, type } = event.data;
  return t("change.storageAccount", {
    user,
    storage: cloudStorageOptions[type].label,
  });
}

function storageAccountAuth(
  event: StorageAccountCreatedEvent | StorageAccountReauthorizedEvent,
  t: TFunction<"userActivity" | "users">
) {
  const { user, type, reason } = event.data;
  const account =
    type === "DROPBOX" && typeof user === "object" && user.type === "business"
      ? t("change.dropbox_business_storageAccount", { user })
      : t("change.storageAccount", {
          user,
          storage: cloudStorageOptions[type].label,
        });

  // The action column already says the account was added: repeating "requested
  // when adding an account" under it is noise. Every other combination is worth
  // showing -- a re-authorization to open the file browser is what MXH-5796 was
  // about, and an account created from there is worth seeing too.
  const saysNothingNew =
    event.type === "storage_account_created" && reason === "add_account";

  // No reason on events written before MXH-5796, nor on authorizations that did
  // not start in the dashboard.
  if (!reason || saysNothingNew) return account;

  return (
    <>
      {account}
      <Typography variant="body2" color="textSecondary">
        {t(`change.storageAuthReason.${reason}`)}
      </Typography>
    </>
  );
}

/** A login option that was turned on, turned off, or reconfigured. */
type SecurityOptionChange = { option: string; change: string };

const LOGIN_OPTIONS = ["GOOGLE", "PASSWORD", "OFFICE", "SAML"] as const;

function securityUpdated(event: SecurityEvent, t, id) {
  // TODO(MXH-5745): legacy branch, see the LEGACY block at the end of the file.
  const changes = isLegacyPayload<LegacySecurityUpdatedData>(
    event.data,
    "updates"
  )
    ? legacySecurityChanges(event.data)
    : securityChanges(
        event.data.previous,
        event.data.current,
        event.data.samlCertChanged
      );

  return (
    <AffectedList>
      {changes.map(({ option, change }) => (
        <li key={`${id}-${option}-${change}`}>
          {change === "saml_changed" ? (
            t("change.security.saml_changed")
          ) : (
            <>
              {t(`change.security.${option}`)} {t(`change.security.${change}`)}
            </>
          )}
        </li>
      ))}
    </AffectedList>
  );
}

/**
 * The event stores the whole `security.auth` section before and after, so what
 * the user actually did is derived here rather than persisted.
 */
function securityChanges(
  previous: LoginOptions,
  current: LoginOptions,
  samlCertChanged?: boolean
): SecurityOptionChange[] {
  const changes: SecurityOptionChange[] = [];

  LOGIN_OPTIONS.forEach(option => {
    const had = Boolean(previous?.[option]);
    const has = Boolean(current?.[option]);
    if (!had && has) changes.push({ option, change: "turn_on" });
    if (had && !has) changes.push({ option, change: "turn_off" });
  });

  // `cert` reaches us redacted, so it reads as `***` on both sides however many
  // times it rotated: that half of the comparison comes from the API, which
  // made it before redacting (see SecurityEvent.samlCertChanged).
  const samlReconfigured =
    previous?.SAML &&
    current?.SAML &&
    (samlCertChanged ||
      JSON.stringify(previous.SAML) !== JSON.stringify(current.SAML));

  if (samlReconfigured) {
    changes.push({ option: "SAML", change: "saml_changed" });
  }

  // Compared as booleans: organizations that never touched the setting have no
  // key stored, and `undefined` vs `false` is not a change the user made.
  const twoFactorBefore = Boolean(previous?.twoFactorAuthEnabled);
  const twoFactorAfter = Boolean(current?.twoFactorAuthEnabled);

  if (twoFactorBefore !== twoFactorAfter) {
    changes.push({
      option: "twoFactorAuthEnabled",
      change: twoFactorAfter ? "turn_on" : "turn_off",
    });
  }

  return changes;
}

function userUpdated(event: UserUpdatedEvent, t) {
  const { type, email } = event.data;
  return t(`change.user_updated.${type}`, { email });
}

function organizationUpdated(event: OrganizationEvent, t: TFunction) {
  // TODO(MXH-5745): legacy branch, see the LEGACY block at the end of the file.
  const current = isLegacyPayload<LegacyOrganizationUpdatedData>(
    event.data,
    "updates"
  )
    ? legacyOrganizationCurrent(event.data)
    : event.data.current;

  const { previous } = event.data;
  const hasTransportAgent =
    "allowedTransportAgent" in previous && "allowedTransportAgent" in current;

  if (hasTransportAgent) {
    return (
      <>
        <Typography>
          {t("common:previous")}: {previous.allowedTransportAgent}
        </Typography>
        <Typography>
          {t("common:new")}: {current.allowedTransportAgent}
        </Typography>
      </>
    );
  }

  return "";
}

/* ==========================================================================
 * ⚠️  LEGACY EVENT SUPPORT — DELETE THIS BLOCK AFTER 2027-08-01
 * ==========================================================================
 *
 * Everything below reads events written **before** MXH-5745. There is no
 * backfill: events live in the transient bucket with a one year expiry
 * (`api/src/libs/events.ts`), so old documents cannot be migrated — they can
 * only be read until they expire on their own. The last one written in the old
 * format expires 12 months after the release of MXH-5745 (sprint 177,
 * 2026-08).
 *
 * ## How to remove it, on or after 2027-08-01
 *
 * 1. Delete this whole block.
 * 2. Delete every call site marked `TODO(MXH-5745)` in this file, keeping the
 *    non-legacy side of each ternary.
 * 3. Same in `ItemMenu.tsx`, which has its own LEGACY block.
 * 4. Delete `api/src/types/eventsLegacy.ts` and its line in `api/index.d.ts`.
 *
 * The legacy shapes themselves are documented in `api/src/types/eventsLegacy.ts`.
 * @see api/MXH-5745-EVENTS-DECISION-TABLE.md §6.8
 * ======================================================================== */

/**
 * LEGACY. Discriminates an old payload by a key only the old shape carries.
 * Every event type has one such key — the new shapes never reuse them.
 */
function isLegacyPayload<T>(data: unknown, key: string): data is T {
  return typeof data === "object" && data !== null && key in data;
}

/**
 * LEGACY. `security_updated` used to store which option was flipped instead of
 * the section itself, and one entry per changed SAML field. Those entries are
 * collapsed into a single "SAML reconfigured" line, as the old UI did.
 */
function legacySecurityChanges(
  data: LegacySecurityUpdatedData
): SecurityOptionChange[] {
  const changes: SecurityOptionChange[] = [];
  let samlReported = false;

  data.updates.forEach(({ option, change }) => {
    if (change === "turn_on" || change === "turn_off") {
      changes.push({ option, change });
    } else if (!samlReported) {
      samlReported = true;
      changes.push({ option: "SAML", change: "saml_changed" });
    }
  });

  return changes;
}

/** LEGACY. `organization_updated` used `updates` where it now uses `current`. */
function legacyOrganizationCurrent(data: LegacyOrganizationUpdatedData) {
  return data.updates;
}

/**
 * LEGACY. `security_policy_updated` and `template_definition_updated` used to
 * store the changed properties of the request body under `updated`; both are
 * N2 now and carry `{ id, name, previousName? }`.
 */
function namedPayload(
  data:
    | { name: string; previousName?: string }
    | LegacySecurityPolicyUpdatedData
    | LegacyTemplateDefinitionUpdatedData
): { name: string; previousName?: string } {
  // TODO(MXH-5745): drop the guard and pass `data` through unchanged.
  if (isLegacyPayload<{ updated: { name: string } }>(data, "updated")) {
    return { name: data.updated.name };
  }
  return data as { name: string; previousName?: string };
}
